import React from 'react';

interface Project {
  id: number;
  title: string;
  description: string;
  technologies: string[];
  image: string;
  githubUrl?: string;
  liveUrl?: string;
  youtubeUrl?: string;
  pdfUrl?: string;
  presentationUrl?: string;
  screenshotUrl?: string;
  itchUrl?: string;
}

const Projects: React.FC = () => {
  const projects: Project[] = [
    {
      id: 1,
      title: "Ethical AI & Mental Health Research (Graduate Coursework Research)",
      description: "Explored how LLMs can be used responsibly as screening tools in spiritual health contexts. Part of a graduate student research team writing a peer-review publication.",
      technologies: ["Responsible AI", "AI Ethics", "Ethical Frameworks", "Research & Academic Writing", "Survey & Study Design"],
      image: "🧠",
      githubUrl: "ADD DELIVERABLES"
    },
    {
      id: 2,
      title: "News Summary Tool - LLM-Powered Personalized News Aggregator (In Progress)",
      description: "Developed a web app that scrapes, summarizes and curates news tailored to each user's interests in areas like AI, finance, and sports. Delivers concise, trend-level summaries using multi-agent LLM orchestration for speed and contextual accuracy. Added personalization via vector embeddings and semantic search, tailoring summaries to user behavior and preferences.",
      technologies: ["LLM Orchestration", "Multi-Agent Workflow", "Web Scraping", "Personalization", "AI/ML"],
      image: "📰",
      githubUrl: "https://github.com/tylerpina/LLM_Dev_Final_Project",
      presentationUrl: "/documents/NewsSummaryToolPresentation.pdf"
    },
    {
      id: 3,
      title: "Implications of AI in Cybersecurity (Graduate Paper)",
      description: "Graduate research analyzing the dual role of AI as both a defensive tool and offensive weapon in cybersecurity. The paper reviews seven recent studies to examine AI-driven threat detection, incident response, adversarial machine learning, and ethical/regulatory challenges, highlighting how AI reshapes both cyber defense strategies and attack methods.",
      technologies: ["Artificial Intelligence", "Machine Learning", "Adversarial ML", "Cybersecurity", "IoT Security", "Ethics & Privacy"],
      image: "🛡️",
      pdfUrl: "/documents/Implications_AI_Cybersecurity_Graduate_Paper.pdf"
    },
    {
      id: 4,
      title: "Digital Entropy-based PRNG for Cryptography",
      description: "Graduate cryptography research on pseudorandom number generation using unconventional entropy sources. Combined cosmic ray flux, AI outputs, and live sports data in a cryptographic pipeline, validated with NIST tests. Demonstrated strong randomness properties close to /dev/urandom, showing the potential of multi-source virtual entropy for secure use.",
      technologies: ["Python", "Cryptography", "Data Engineering", "Security Engineering", "Entropic Sources"],
      image: "🔐",
      githubUrl: "https://github.com/nickiversonnn/PRNGMultipleSources",
      pdfUrl: "/documents/Cryptography_PRNG_Project_Final_Paper.pdf"
    },
    {
      id: 5,
      title: "Energy Royalty Partners: Wind Turbine Attribute Regression Model",
      description: "A data pipeline in Python to process satellite imagery and bounding box inputs for downstream ML models to predict wind turbine attributes.",
      technologies: ["Python", "scikit-learn", "Keras", "Machine Learning", "Neural Networks"],
      image: "𖣘",
      presentationUrl: "/documents/WindVisionPresentation.pdf"
    },
    {
      id: 6,
      title: "Ape Alley - 2D Multiplayer Game",
      description: "A real-time 2D multiplayer game with player chat, customizable avatars, and interactive minigames in a Club Penguin-style world, using Unity and C#.",
      technologies: ["Unity", "C#", "Multiplayer Networking", "Game Design", "UI/UX"],
      image: "🎮",
      githubUrl: "https://github.com/stlaurentel/Ape_Alley",
      youtubeUrl: "https://www.youtube.com/watch?v=tn-lsZaX9Sk",
      itchUrl: "https://niverson.itch.io/ape-alley"
    },
    {
      id: 7,
      title: "Smart Travel Planner API",
      description: "A backend API that recommends travel destinations based on flight availability, weather data, and safety scores.",
      technologies: ["Python", "FastAPI", "JSON", "RESTful APIs", "Docker", "Backend Development"],
      image: "✈️",
      githubUrl: "https://github.com/nickiversonnn/smart-travel-planner",
      screenshotUrl: "/images/travel-planner-screenshot.jpg"
    }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Projects</h1>
        <p className="text-xl text-gray-600">Here are some of the projects I've worked on</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {projects.map((project) => (
          <div
            key={project.id}
            className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col h-full"
          >
            {/* Project Image */}
            <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <span className="text-6xl">{project.image}</span>
            </div>

            {/* Project Content */}
            <div className="p-6 flex flex-col flex-grow">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {project.title}
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed flex-grow">
                {project.description}
              </p>

              {/* Technologies */}
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Project Links */}
              <div className="flex gap-3 mt-auto">
                {project.githubUrl && project.githubUrl !== "ADD DELIVERABLES" && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-gray-900 text-white text-center py-3 px-4 rounded-md hover:bg-gray-800 transition-colors duration-200 text-sm font-medium min-h-[44px] flex items-center justify-center"
                  >
                    GitHub
                  </a>
                )}
                {project.screenshotUrl && (
                  <a
                    href={project.screenshotUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-green-600 text-white text-center py-3 px-4 rounded-md hover:bg-green-700 transition-colors duration-200 text-sm font-medium min-h-[44px] flex items-center justify-center"
                  >
                    Screenshot
                  </a>
                )}
                {project.youtubeUrl && (
                  <a
                    href={project.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-red-600 text-white text-center py-3 px-4 rounded-md hover:bg-red-700 transition-colors duration-200 text-sm font-medium min-h-[44px] flex items-center justify-center"
                  >
                    YouTube Trailer
                  </a>
                )}
                {project.pdfUrl && (
                  <a
                    href={project.pdfUrl}
                    download
                    className="flex-1 bg-purple-600 text-white text-center py-3 px-4 rounded-md hover:bg-purple-700 transition-colors duration-200 text-sm font-medium min-h-[44px] flex items-center justify-center"
                  >
                    Download PDF
                  </a>
                )}
                {project.presentationUrl && (
                  <a
                    href={project.presentationUrl}
                    download
                    className="flex-1 bg-indigo-600 text-white text-center py-3 px-4 rounded-md hover:bg-indigo-700 transition-colors duration-200 text-sm font-medium min-h-[44px] flex items-center justify-center"
                  >
                    Download Presentation
                  </a>
                )}
                {project.itchUrl && (
                  <a
                    href={project.itchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-orange-600 text-white text-center py-3 px-4 rounded-md hover:bg-orange-700 transition-colors duration-200 text-sm font-medium min-h-[44px] flex items-center justify-center"
                  >
                    Download on itch.io
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Projects; 
